import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import {
  Alert,
  Card,
  Filters,
  Page,
  PageHeader,
  Pagination,
  type FilterFieldSlot,
} from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';
import { MirrorStamp } from './common.js';
import { HistoryRefreshButton, ServiceRecordsTable } from './history.js';

/**
 * O histórico de ATENDIMENTO da praça, como o SGA o registrou (14/10/2026).
 *
 * A esteira traz os atendimentos continuamente, do último sucesso para cá —
 * a primeira passagem, os últimos 30 dias. O histórico INTEIRO de um associado
 * chega pelo "Atualizar do ERP" da ficha dele, e por isso esta lista pode ter
 * atendimento antigo de quem foi aberto e recente de todo mundo. O cartão diz
 * isso, em vez de a lista parecer completa.
 */
export function ServiceRecordsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/atendimentos' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const applied = {
    from: search.from ?? '',
    until: search.until ?? '',
    typeCode: search.typeCode ?? '',
    statusCode: search.statusCode ?? '',
    departmentCode: search.departmentCode ?? '',
  };
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'atendimentos', tenantId, term, applied, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchServiceRecords({
        body: {
          term: term.length >= 2 ? term : undefined,
          from: applied.from || undefined,
          until: applied.until || undefined,
          typeCode: applied.typeCode || undefined,
          statusCode: applied.statusCode || undefined,
          departmentCode: applied.departmentCode || undefined,
          page,
          perPage,
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os atendimentos'));
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const filters = useQuery({
    queryKey: ['erp', 'atendimentos', 'filtros', tenantId],
    queryFn: async () => {
      const r = await api.erp.serviceRecordFilters();
      return r.status === 200 ? r.body : null;
    },
  });

  const options = (list: { code: string; label: string | null }[] | undefined) =>
    (list ?? []).map((v) => ({ value: v.code, label: v.label ?? `Código ${v.code}` }));

  const fields: FilterFieldSlot[] = [
    { type: 'intervalo', label: 'Registrado em', from: 'from', to: 'until' },
    {
      type: 'selecao',
      name: 'typeCode',
      label: 'Tipo',
      placeholder: 'Todos',
      options: options(filters.data?.types),
    },
    {
      type: 'selecao',
      name: 'departmentCode',
      label: 'Departamento',
      placeholder: 'Todos',
      options: options(filters.data?.departments),
    },
    {
      type: 'selecao',
      name: 'statusCode',
      label: 'Status',
      placeholder: 'Todos',
      options: options(filters.data?.statuses),
    },
  ];

  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);
  const filtered = Boolean(term || Object.values(applied).some(Boolean));

  return (
    <Page measure="cheia">
      <PageHeader title="Atendimentos">
        O histórico de atendimento de {context?.currentTenant.nome}, como o ERP o
        registra.
      </PageHeader>

      <HistoryRefreshButton
        entity="atendimento"
        hint="traz os atendimentos registrados no SGA desde a última passagem"
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} atendimento(s)`}
          descricao={
            <>
              <MirrorStamp at={result?.syncedAt ?? null} /> · a esteira traz os dos
              últimos dias; o histórico inteiro de um associado vem pela ficha dele
            </>
          }
          bleed
        >
          <ServiceRecordsTable
            items={result?.estado === 'lida' ? result.items : []}
            loading={query.isLoading}
            withMember
            empty={
              filtered
                ? 'Nenhum atendimento com esses filtros.'
                : 'Nenhum atendimento no cadastro desta base ainda.'
            }
            search={{
              label: 'Pesquisar atendimentos',
              placeholder: 'Nome, CPF/CNPJ ou código do associado, ou o título',
              value: text,
              onChange: setText,
            }}
            toolbar={
              <Filters
                values={applied}
                fields={fields}
                onApply={(values) =>
                  list.setFilters({
                    from: String(values.from ?? '') || undefined,
                    until: String(values.until ?? '') || undefined,
                    typeCode: String(values.typeCode ?? '') || undefined,
                    statusCode: String(values.statusCode ?? '') || undefined,
                    departmentCode: String(values.departmentCode ?? '') || undefined,
                  })
                }
              />
            }
          />
          {result?.estado === 'lida' && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="atendimentos"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
